import React from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import { useUntypedTranslation } from '../../../hooks/useUntypedTranslation';
import { pageSurface, heading, meta } from '../../../styles/visualLanguage';
import type { AdvisoryAnswerProgressRow } from '../advisoryAnswerView';
import { AdvisoryLeaveButton } from './AdvisoryLeaveButton';

export function AdvisoryAnswerProgress({
  rows,
  embedded = false,
}: {
  rows: readonly AdvisoryAnswerProgressRow[];
  embedded?: boolean;
}) {
  const { t } = useUntypedTranslation();
  const terminal = new Set([
    'answered',
    'completed',
    'partial',
    'unanswerable',
    'failed',
    'skipped',
  ]);
  const done = rows.filter(row => terminal.has(row.answerStatus || row.status)).length;
  const total = rows.length;
  const percent = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;

  return (
    <Box
      sx={[
        pageSurface,
        {
          p: embedded ? 0 : 2.5,
          width: '100%',
          maxWidth: embedded ? 'none' : 760,
          marginX: 'auto',
        },
      ]}
    >
      {!embedded && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <AdvisoryLeaveButton />
        </Box>
      )}
      <Box role="status" aria-live="polite">
        <Typography component={embedded ? 'h2' : 'h1'} sx={[heading]}>
          {t('advisory:answers.progressTitle')}
        </Typography>
        <Typography sx={[meta]}>{t('advisory:answers.progressHint')}</Typography>
        {total > 0 ? (
          <>
            <Typography sx={[meta]} data-testid="advisory-answer-progress-count">
              {t('advisory:answers.progressCount', { done, total })}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={percent}
              aria-hidden
              sx={{ marginY: 2, borderRadius: 1 }}
            />
          </>
        ) : (
          <Typography sx={[meta]}>{t('advisory:answers.waitingForGraph')}</Typography>
        )}
        <Box component="ul" sx={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {rows.map(row => {
            const status = row.answerStatus || row.status || 'queued';
            const isChild = Boolean(row.parent) || row.kind === 'subquestion';
            return (
              <Box
                component="li"
                key={row.label}
                data-answer-label={row.label}
                data-answer-status={status}
                sx={theme => ({
                  marginLeft: isChild ? { xs: 2, sm: 5 } : 0,
                  paddingY: 1.5,
                  borderBottom: `1px solid ${theme.visualPrimitives.border}`,
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) auto',
                  gap: 2,
                })}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={theme => ({ color: theme.colors.inkPrimary })}>
                    {row.question || row.label}
                  </Typography>
                  {row.phase ? (
                    <Typography sx={[meta]} data-answer-phase={row.phase}>
                      {t('advisory:answers.phase', { phase: row.phase })}
                    </Typography>
                  ) : null}
                </Box>
                <Typography sx={[meta]}>{t('advisory:answers.status', { status })}</Typography>
              </Box>
            );
          })}
        </Box>
        <Typography sx={[meta]}>{t('advisory:thinking.leaveAndReturn')}</Typography>
      </Box>
    </Box>
  );
}

export default AdvisoryAnswerProgress;
